import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[sgtPercentInput]'
})
export class PercentInputDirective {
  @Input() decimals = 2;
  @Input() max: number | null = null;
  @Input() allowEmpty = true;
  @Input() allowNegative = false;

  private readonly ALLOWED_SPECIAL_KEYS = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Meta',
    'Command'
  ];

  private readonly PARTIAL_VALUES = ['', '-'];

  constructor(private elementRef: ElementRef<HTMLInputElement>) {}

  /**
   * Intercepta teclas e bloqueia caracteres inválidos
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isAllowed =
      this.ALLOWED_SPECIAL_KEYS.includes(event.key) ||
      this.isShortcutKey(event) ||
      this.isDigit(event.key) ||
      this.isDecimalSeparator(event.key) ||
      this.isMinusSign(event.key);

    if (!isAllowed) {
      event.preventDefault();
    }
  }

  /**
   * Tratativa de colar de texto, tratando o valor colado
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = this.elementRef.nativeElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;

    const merged =
      input.value.substring(0, start) + pasted + input.value.substring(end);
    const sanitized = this.sanitize(merged);

    this.updateInput(sanitized, start + pasted.length);
  }

  /**
   * Sanitiza o valor após cada alteração no input
   */
  @HostListener('input')
  onInput(): void {
    const input = this.elementRef.nativeElement;
    const cursorPos = input.selectionStart ?? input.value.length;
    const sanitized = this.sanitize(input.value);

    if (input.value !== sanitized) {
      const diff = input.value.length - sanitized.length;
      this.updateInput(sanitized, Math.max(0, cursorPos - diff));
    }
  }

  /**
   * Sanitiza string para formato percentual válido (ex: "12,50")
   */
  private sanitize(raw: string): string {
    if (!raw) {
      return this.allowEmpty ? '' : '0';
    }

    let value = this.normalizeDecimalSeparator(raw);
    value = this.removeInvalidChars(value);
    value = this.keepFirstCommaOnly(value);
    value = this.removeLeadingZeros(value);
    value = this.limitDecimals(value);
    value = this.handleEdgeCases(value);
    value = this.applyMax(value);

    return value;
  }

  /** Converte ponto para vírgula */
  private normalizeDecimalSeparator(value: string): string {
    return value.replace(/\./g, ',');
  }

  /** Remove caracteres que não são dígitos, vírgula ou sinal de menos */
  private removeInvalidChars(value: string): string {
    if (!this.allowNegative) {
      return value.replace(/[^0-9,]/g, '');
    }

    const hasNegative = value.startsWith('-');
    const cleaned = value.replace(/[^0-9,]/g, '');
    return hasNegative ? `-${cleaned}` : cleaned;
  }

  /** Mantém apenas a primeira vírgula */
  private keepFirstCommaOnly(value: string): string {
    const commaIndex = value.indexOf(',');
    if (commaIndex === -1) {
return value;
}

    const integerPart = value.slice(0, commaIndex);
    const decimalPart = value.slice(commaIndex + 1).replace(/,/g, '');
    return `${integerPart},${decimalPart}`;
  }

  /** Remove zeros à esquerda, preservando "0" e "0," */
  private removeLeadingZeros(value: string): string {
    const isNegative = value.startsWith('-');
    let result = isNegative ? value.slice(1) : value;

    result = result.replace(/^0+(?=\d)/, '');
    if (result.startsWith(',')) {
      result = `0${result}`;
    }

    return isNegative ? `-${result}` : result;
  }

  /** Limita casas decimais conforme input */
  private limitDecimals(value: string): string {
    if (this.decimals < 0 || !value.includes(',')) {
return value;
}

    const [intPart, decPart = ''] = value.split(',');
    return `${intPart},${decPart.slice(0, this.decimals)}`;
  }

  /** Trata casos especiais (vírgula sozinha, valor vazio, sinal de menos) */
  private handleEdgeCases(value: string): string {
    if (value === ',') {
return '0,';
}
    if (value === '-,') {
return '-0,';
}
    if (!this.allowEmpty && value === '') {
return '0';
}
    return value;
  }

  /** Aplica valor máximo se houver input de max */
  private applyMax(value: string): string {
    if (this.max === null || this.isPartialValue(value)) {
      return value;
    }

    const num = parseFloat(value.replace(',', '.'));
    return num > this.max ? String(this.max).replace('.', ',') : value;
  }

  /** Verifica se é um valor parcial/incompleto (ainda sendo digitado) */
  private isPartialValue(value: string): boolean {
    return this.PARTIAL_VALUES.includes(value) || value.endsWith(',');
  }

  /** Atualiza valor do input e posição do cursor */
  private updateInput(value: string, cursorPos: number): void {
    const input = this.elementRef.nativeElement;
    if (input.value === value) {
      input.setSelectionRange(cursorPos, cursorPos);
      return;
    }

    input.value = value;
    const pos = Math.min(cursorPos, value.length);
    input.setSelectionRange(pos, pos);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** Verifica se é numero (0-9) */
  private isDigit(key: string): boolean {
    return /^[0-9]$/.test(key);
  }

  /** Verifica se é separador decimal (vírgula ou ponto) */
  private isDecimalSeparator(key: string): boolean {
    return key === ',' || key === '.';
  }

  /** Verifica se é atalho de teclado (Ctrl/Cmd + A/C/V/X/Z) */
  private isShortcutKey(event: KeyboardEvent): boolean {
    const shortcutKeys = ['a', 'c', 'v', 'x', 'z'];
    const key = event.key.toLowerCase();
    return (event.metaKey || event.ctrlKey) && shortcutKeys.includes(key);
  }

  /** Verifica se é sinal de menos (quando permitido) */
  private isMinusSign(key: string): boolean {
    return this.allowNegative && key === '-';
  }
}
